import { AttributeExtendedClient } from '@frontend/attribute/api';
import { AttributeExtended, AttributeExtendedLinkedProduct } from '@frontend/attribute/types';
import { EntityType } from '@frontend/common';
import { DefaultSecondaryButton, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { refresh } from '@frontend/repository';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { LinkProductsModal } from '../../applications/modals/attribute-detail-modal/link-products-modal/link-products-modal.component';
import { TranslationContainer } from '../../applications/modals/containers/translation-container/translations-container.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { triggerRefetch } from '../../redux/detail-modal-slice';
import { useAppDispatch } from '../../redux/store';

const ATTRIBUTE_LIST_ID = 'attribute-detail-list';
const LINK_PRODUCTS_MODAL_ID = 'link-attribute-products-modal';

export const ATTRIBUTE_LINKED_LABEL = (
    <FormattedMessage
        id='attribute_detail_modal.linked_products'
        defaultMessage='Linked products'
    />
);

export const AttributeDetailsCard = ({ attribute }: { attribute: AttributeExtended }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
            <PlatformDetailGrid
                object={attribute}
                cols={2}
                order={['name', 'num_linked_products', 'values']}
                keyOverwrite={new Map([['num_linked_products', ATTRIBUTE_LINKED_LABEL]])}
                valueOverwrite={
                    new Map<string, (obj: AttributeExtended) => React.ReactNode>([
                        ['name', (obj) => (locale ? getCorrectTranslation(locale, obj, 'name') : obj.name)],
                        [
                            'values',
                            (obj) => (
                                <div className='flex gap-1'>
                                    {obj.values &&
                                        obj.values.map((value: string) => (
                                            <div
                                                key={value}
                                                className='bg-border-default inline-flex h-6 min-w-6 items-center justify-center gap-2.5 rounded px-1'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>{value}</div>
                                            </div>
                                        ))}
                                </div>
                            )
                        ]
                    ])
                }
            />
        </div>
    );
};

export const AttributeGeneralSection = ({ attribute }: { attribute: AttributeExtended }) => (
    <TranslationContainer
        object={attribute}
        ContainerTitle={CommonMessage.DETAIL.TRANSLATIONS}
        HeaderList={[CommonMessage.FORMS.NAME]}
        translations={['name']}
    />
);

export const AttributeLinkedSection = ({ attribute }: { attribute: AttributeExtended }) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModals();

    const refreshAttributeList = () => {
        dispatch(refresh({ listId: ATTRIBUTE_LIST_ID, refresh: true }));
    };

    const handleLinkProductsSuccess = () => {
        refreshAttributeList();
        dispatch(triggerRefetch({ entityType: EntityType.ATTRIBUTE, entityId: attribute.id }));
    };

    return (
        <ReactiveObjectList<AttributeExtendedLinkedProduct>
            fetch={() => AttributeExtendedClient.fetchAttributeLinkedProducts(attribute.account_id, attribute.id)}
            id={ATTRIBUTE_LIST_ID}
            useStaticListId
            type={EntityType.PRODUCT_ATTRIBUTE}
            dispatch={dispatch}
            columns={{
                columns: ['product', 'ean', 'sku', 'value'],
                customCells: new Map([
                    [
                        'product',
                        (row) =>
                            row.row.original.product && (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        entityType={EntityType.PRODUCT}
                                        id={row.row.original.product.entity_id}
                                        display={row.row.original.product.name}
                                        foreign={row.row.original.product.foreign}
                                        accountId={accountIdFromResolvedEntity(row.row.original.product)}
                                    />
                                </td>
                            )
                    ]
                ])
            }}
            tableProps={{
                defaultHeight: true,
                customFooter: (
                    <div>
                        <DefaultSecondaryButton
                            text={
                                <FormattedMessage
                                    id='category-detail-modal.link-products-button'
                                    defaultMessage='Link products'
                                />
                            }
                            onClick={() =>
                                openModal(LINK_PRODUCTS_MODAL_ID, () => (
                                    <LinkProductsModal
                                        successCallback={handleLinkProductsSuccess}
                                        attributeId={attribute.id}
                                    />
                                ))
                            }
                        />
                    </div>
                )
            }}
        />
    );
};
